/**
 * useGridDimensions
 * Responsive grid column count and screen width with Dimensions subscription.
 * Debounced on web to avoid excessive re-renders on resize.
 */

import { useState, useEffect, useRef } from 'react'
import { Dimensions, Platform } from 'react-native'
import { debounce } from '../utils/debounce'
import { calculateColumns } from '../helpers/calculateColumns.helper'

export interface UseGridDimensionsOptions {
  /** When set, use this width instead of listening to Dimensions (e.g. from parent). */
  screenWidth?: number
}

export interface UseGridDimensionsReturn {
  numColumns: number
  screenWidth: number
}

export function useGridDimensions(
  options: UseGridDimensionsOptions = {}
): UseGridDimensionsReturn {
  const { screenWidth: propScreenWidth } = options

  const [numColumns, setNumColumns] = useState(() => {
    const width = propScreenWidth ?? Dimensions.get('window').width
    return calculateColumns(width)
  })
  const [screenWidth, setScreenWidth] = useState(
    propScreenWidth ?? Dimensions.get('window').width
  )

  const debouncedUpdateRef = useRef(
    debounce((width: number) => {
      setNumColumns(calculateColumns(width))
      setScreenWidth(width)
    }, Platform.OS === 'web' ? 150 : 0)
  )

  useEffect(() => {
    if (propScreenWidth !== undefined) {
      setNumColumns(calculateColumns(propScreenWidth))
      setScreenWidth(propScreenWidth)
      return
    }

    const updateDimensions = () => {
      const { width } = Dimensions.get('window')
      if (Platform.OS === 'web') {
        debouncedUpdateRef.current(width)
      } else {
        setNumColumns(calculateColumns(width))
        setScreenWidth(width)
      }
    }

    updateDimensions()
    const subscription = Dimensions.addEventListener('change', updateDimensions)
    const debouncedUpdate = debouncedUpdateRef.current

    return () => {
      subscription?.remove()
      debouncedUpdate.cancel()
    }
  }, [propScreenWidth])

  return { numColumns, screenWidth }
}
